import React, { useRef, useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { toastProperties } from "../../../commons/Toast/Toaster";
import {
  addGenerator,
  updateGenerator,
} from "../../../Services/CompanyAdmin/AllApiAdmin";
import {
  addGeneratorSuperAdmin,
  updateGeneratorSuperAdmin,
} from "../../../Services/GNA/AlterApi";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConsumer,
  fetchConsumers,
  fetchGeneratorDetails,
  fetchGenerators,
} from "../../../Redux/Slices/companyAdminSlice";
import { fetchGeneratorsCompanyId } from "../../../Redux/Slices/gnaAdminSlice";
import Styles from "../../Auth/Register/Elements/Element.module.css";
import Fileupload from "../../../commons/Inputs/FileUpload/Fileupload";
import SelectInput from "../../../commons/Inputs/Select/SelectInput";
import DateInput from "../../../commons/Inputs/DateInput/DateInput";
import TextInput from "../../../commons/Inputs/TextInput/TextInput";
import { Add, Close } from "@mui/icons-material";
import { verifyGeneratorName } from "../../../Services/ApiCalling/Auth";
import { decryptData } from "../../../commons/Decode";
import {
  publicCityById,
  publicDiscomByStateId,
  publicStateByRegionId,
  publicEnergyType,
  publicVoltage,
  publicRegion,
  publicDiscom,
  publicStateUT,
  publicSpecefication,
  publicControlArea,
} from "../../../Redux/Slices/publicSlice";
import { ReactComponent as RemoveIcon } from "../../../assets/images/Remove.svg";
import { fetchSideBarGeneratorsList } from "../../../Redux/Slices/CapacitySlice";
import { getWalletAccountsByCompanies } from "../../../Services/Payments/PaymentApi";
import { useLocation } from "react-router-dom";

const AddressSection = ({
  prefix,
  addressData,
  handleChange,
  errors,
  publicData,
  index,
}) => {
  const dispatch = useDispatch();

  const handleGetCityByStateId = useCallback(
    (index, field, value) => {
      dispatch(publicCityById(value?.id));
      handleChange(index, field, value);
    },
    [dispatch, handleChange],
  );

  const renderTextInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false,
  ) => (
    <TextInput
      heading={`${heading} `}
      value={addressData[field]}
      onChange={(value) => handleChange(index, field, value)}
      example={example}
      type={type}
      height="45"
      error={errors[`${field}_${index}`]}
      readOnly={readOnly}
    />
  );

  const renderSelectInput = (field, heading, list, example) => (
    <SelectInput
      onChange={(value) =>
        heading === "State"
          ? handleGetCityByStateId(index, field, value)
          : handleChange(index, field, value)
      }
      list={list}
      value={addressData[field]}
      example={example}
      heading={`${heading} `}
      height="45px"
      error={errors[`${field}_${index}`]}
    />
  );

  return (
    <>
      <div className={Styles.layer1ElementsContainer}>
        {renderTextInput(`${prefix}_address`, "Full Address", `Enter Address`)}
      </div>
      <div className={Styles.layer3ElementsContainer}>
        {renderTextInput(`${prefix}_pincode`, "Pin Code", "Enter Pincode")}
        {renderSelectInput(
          `${prefix}_state`,
          "State",
          publicData?.stateUT,
          "Eg. Select State",
        )}
        {renderSelectInput(
          `${prefix}_city`,
          "City",
          publicData?.city,
          "Eg. Select City",
        )}
      </div>

      <div className={Styles.layer2ElementsContainer}>
        {renderTextInput(
          `${prefix}_contact`,
          "Name of the Contact Person",
          "Enter Contact Person Name",
        )}
        {renderTextInput(`${prefix}_email`, "Email", "Enter Email")}
      </div>
      <div className={Styles.layer2ElementsContainer}>
        {renderTextInput(`${prefix}_mobile`, "Mobile No.", "Enter Mobile No.")}
        {renderTextInput(
          `${prefix}_altMobile`,
          "Alternate Mobile No.",
          "Enter Alternate Mobile No.",
        )}
      </div>
    </>
  );
};

const CompanyAdminGeneratorForm = ({
  handleClose,
  onSwitchToConsumer,
  editMode = false,
  entityData = null,
  entityId = null,
  isModal = false,
  isSuperAdmin = false,
  companyId = null,
  hideSelection = false,
}) => {
  const dispatch = useDispatch();
  const [sameCheck, setSameCheck] = useState("same");
  const [loading, setLoading] = useState(false);
  const [sameBank, setSameBank] = useState(true);

  const publicData = useSelector((state) => state?.publicSlice);
  const authUser = useSelector((state) => state?.persistedReducer?.user);

  const initialGeneratorDetail = useCallback(() => {
    if (editMode && entityData) {
      const generatorData =
        entityData?.data?.data || entityData?.data || entityData;

      const formatCommissionedDate = (dateValue) => {
        if (!dateValue) return "";

        if (typeof dateValue === "string" && dateValue.includes("-")) {
          return dateValue;
        }

        if (Array.isArray(dateValue) && dateValue.length === 3) {
          const [year, month, day] = dateValue;
          return `${year}-${month.toString().padStart(2, "0")}-${day
            .toString()
            .padStart(2, "0")}`;
        }

        return "";
      };

      const officeAddress = generatorData.generatorOfficeAddress || {};
      const correspondenceAddress =
        generatorData.generatorCorrespondenceAddress || {};

      const specificationObj = generatorData.specificationId
        ? publicData?.specification?.find(
            (spec) => spec.id === generatorData.specificationId,
          ) || {
            id: generatorData.specificationId,
            name: generatorData.specificationName || "Unknown",
          }
        : {};

      const energyTypeObj = generatorData.energyTypeId
        ? publicData?.publicEnergyType?.find(
            (energy) => energy.id === generatorData.energyTypeId,
          ) || {
            id: generatorData.energyTypeId,
            name: generatorData.energyTypeName || "Unknown",
          }
        : {};

      const stateObj = generatorData.stateUtId
        ? publicData?.stateUT?.find(
            (state) => state.id === generatorData.stateUtId,
          ) || {
            id: generatorData.stateUtId,
            name: generatorData.stateUtName || "Unknown",
          }
        : {};

      const controlAreaObj =
        generatorData.controlAreaId && generatorData.controlAreaId !== 0
          ? publicData?.controlArea?.find(
              (area) => area.id === generatorData.controlAreaId,
            ) || {
              id: generatorData.controlAreaId,
              name: `Control Area ${generatorData.controlAreaId}`,
            }
          : {};

      const registeredStateObj = officeAddress.stateUtId
        ? publicData?.stateUT?.find(
            (state) => state.id === officeAddress.stateUtId,
          ) || {
            id: officeAddress.stateUtId,
            name: officeAddress.stateUtName || "Unknown",
          }
        : {};

      const registeredCityObj = officeAddress.cityId
        ? publicData?.city?.find(
            (city) => city.id === officeAddress.cityId,
          ) || {
            id: officeAddress.cityId,
            name: officeAddress.cityName || "Unknown",
          }
        : {};

      const correspondenceStateObj = correspondenceAddress.stateUtId
        ? publicData?.stateUT?.find(
            (state) => state.id === correspondenceAddress.stateUtId,
          ) || {
            id: correspondenceAddress.stateUtId,
            name: correspondenceAddress.stateUtName || "Unknown",
          }
        : {};

      const correspondenceCityObj = correspondenceAddress.cityId
        ? publicData?.city?.find(
            (city) => city.id === correspondenceAddress.cityId,
          ) || {
            id: correspondenceAddress.cityId,
            name: correspondenceAddress.cityName || "Unknown",
          }
        : {};

      const result = {
        plantName: generatorData.generatorName || "",
        plant: generatorData.isCommissioned
          ? "commissioned"
          : "notcommissioned",
        plantDate: formatCommissionedDate(generatorData.commissionedAt) || "",
        genType: "",
        genId: energyTypeObj,
        specification: specificationObj,
        installCap: generatorData.installedCap?.toString() || "",
        injectionCap: generatorData.maxInjCap?.toString() || "",
        identificationCode: generatorData.uniqueId || "",
        subsidy: generatorData.isSubsidyReceived ? "YES" : "NO",
        subsidyType: generatorData.subsidyType || "",
        abt: generatorData.isAbtExist ? "EXISTS" : "NON_EXISTS",
        controlArea: controlAreaObj,
        poi: generatorData.pointValue || "",
        poiLocation: generatorData.poiLocation || "",
        region: {},
        state: stateObj,
        discom: Array.isArray(generatorData.discomDetails)
          ? generatorData.discomDetails.map((item) => {
              const discomObj = publicData?.discom?.find(
                (discom) => discom.id === item.discomId,
              ) || {
                id: item.discomId,
                name: `DISCOM ${item.discomId}`,
                acronym: `DISCOM ${item.discomId}`,
              };

              return {
                state: stateObj,
                discomId: discomObj,
                voltage: item.voltageLevel
                  ? {
                      level: item.voltageLevel,
                      name: item.voltageLevel,
                      id: item.voltageLevel,
                    }
                  : null,
              };
            })
          : [],
        discomName: {},
        discomVoltage: [],
        voltage: {},
        istsVoltage: Array.isArray(generatorData.istsVoltageLevel)
          ? generatorData.istsVoltageLevel.map((voltage) => {
              if (typeof voltage === "string") {
                return { level: voltage, name: voltage, id: voltage };
              } else if (typeof voltage === "number") {
                return {
                  level: voltage.toString(),
                  name: voltage.toString(),
                  id: voltage,
                };
              }
              return voltage;
            })
          : [],
        stuVoltage: Array.isArray(generatorData.stuVoltageLevel)
          ? generatorData.stuVoltageLevel.map((voltage) => {
              if (typeof voltage === "string") {
                return { level: voltage, name: voltage, id: voltage };
              } else if (typeof voltage === "number") {
                return {
                  level: voltage.toString(),
                  name: voltage.toString(),
                  id: voltage,
                };
              }
              return voltage;
            })
          : [],
        stateList: [],
        discomList: [],
        bankName: generatorData?.generatorBankDetails?.bankName || "",
        branchAddress: generatorData?.generatorBankDetails?.branchAddress || "",
        accountNumber: generatorData?.generatorBankDetails?.accountNumber || "",
        ifscCode: generatorData?.generatorBankDetails?.ifscCode || "",
        bankDifferent: true,
        pan: generatorData?.pan || "",
        gst: generatorData?.gst || "",
        tan: generatorData?.tan || "",
        panDocId: generatorData?.generatorDocuments?.panDocId || "",
        gstDocId: generatorData?.generatorDocuments?.gstDocId || "",
        tanDocId: generatorData?.generatorDocuments?.tanDocId || "",
        registered_address: officeAddress.address || "",
        registered_state: registeredStateObj,
        registered_city: registeredCityObj,
        registered_pincode: officeAddress.pinCode?.toString() || "",
        registered_tel: "",
        registered_email: officeAddress.email || "",
        registered_fax: "",
        registered_contact: officeAddress.contactPersonName || "",
        registered_contactPhone: "",
        registered_mobile: officeAddress.mobileNumber || "",
        registered_altMobile: officeAddress.alternateMobileNumber || "",
        correspondence_address: correspondenceAddress.address || "",
        correspondence_state: correspondenceStateObj,
        correspondence_city: correspondenceCityObj,
        correspondence_pincode: correspondenceAddress.pinCode?.toString() || "",
        correspondence_tel: "",
        correspondence_email: correspondenceAddress.email || "",
        correspondence_fax: "",
        correspondence_contact: correspondenceAddress.contactPersonName || "",
        correspondence_contactPhone: "",
        correspondence_mobile: correspondenceAddress.mobileNumber || "",
        correspondence_altMobile:
          correspondenceAddress.alternateMobileNumber || "",
      };
      return result;
    }

    return {
      plantName: "",
      plant: "",
      plantDate: "",
      genType: "",
      genId: {},
      specification: {},
      installCap: "",
      injectionCap: "",
      identificationCode: "",
      subsidy: "",
      subsidyType: "",
      abt: "",
      controlArea: {},
      poi: "",
      poiLocation: "",
      region: {},
      state: {},
      discom: [],
      discomName: {},
      discomVoltage: [],
      voltage: {},
      istsVoltage: [],
      stuVoltage: [],
      stateList: [],
      discomList: [],
      bankName: "",
      branchAddress: "",
      accountNumber: "",
      ifscCode: "",
      bankDifferent: true,
      pan: "",
      gst: "",
      tan: "",
      panDocId: "",
      gstDocId: "",
      tanDocId: "",
      registered_address: "",
      registered_state: {},
      registered_city: {},
      registered_pincode: "",
      registered_tel: "",
      registered_email: "",
      registered_fax: "",
      registered_contact: "",
      registered_contactPhone: "",
      registered_mobile: "",
      registered_altMobile: "",
      correspondence_address: "",
      correspondence_state: {},
      correspondence_city: {},
      correspondence_pincode: "",
      correspondence_tel: "",
      correspondence_email: "",
      correspondence_fax: "",
      correspondence_contact: "",
      correspondence_contactPhone: "",
      correspondence_mobile: "",
      correspondence_altMobile: "",
    };
  }, [
    editMode,
    entityData,
    publicData?.specification,
    publicData?.publicEnergyType,
    publicData?.stateUT,
    publicData?.city,
    publicData?.discom,
    publicData?.controlArea,
  ]);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const companyIdFromUrl = searchParams.get("id");

  const [errors, setErrors] = useState({});
  const [generatorDetails, setGeneratorDetails] = useState([
    initialGeneratorDetail(),
  ]);
  const [discomVoltage, setDiscomVoltage] = useState([
    { discomId: null, voltage: null },
  ]);

  const userData = useSelector((state) => state?.persistedReducer);
  const companyConGenId = userData?.company?.companyId || companyIdFromUrl;
  const userConGenId = userData?.user?.user?.userId || companyIdFromUrl;

  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeType, setActiveType] = useState(null);

  const [consumerOptions, setConsumerOptions] = useState([]);
  const [generatorOptions, setGeneratorOptions] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [activeRelation, setActiveRelation] = useState("DIFFERENT");

  const [selectedConsumer, setSelectedConsumer] = useState(null);
  const [selectedGenerator, setSelectedGenerator] = useState(null);

  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const [sameAsCompanyData, setSameAsCompanyData] = useState(null);

  const debounceTimeout = useRef(null);
  const handleClick = async (type) => {
    setActiveType(type);
    setOpenDropdown(openDropdown === type ? null : type); // Toggle dropdown

    if (type === "CONSUMER") setSelectedConsumer(null);
    if (type === "GENERATOR") setSelectedGenerator(null);

    const targetCompanyId = companyId || companyIdFromUrl || companyConGenId;

    if (!targetCompanyId) {
      setError("Company ID is not set");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await getWalletAccountsByCompanies([targetCompanyId]);

      const fetchedCompanyData = res?.data?.data?.find(
        (item) => item?.companyId == targetCompanyId, // Use == for loose comparison
      );

      if (!fetchedCompanyData) {
        setError("Company data not found");
        return;
      }

      setCompanyData(fetchedCompanyData);
    } catch (err) {
      console.error("Wallet Api error", err);
      setError("Failed to fetch wallet data: " + err.message);
      setConsumerOptions([]);
      setGeneratorOptions([]);
      setCompanyData(null); 
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchRegions = useCallback(() => {
    if (!publicData?.region?.length) {
      dispatch(publicRegion());
    }
  }, [publicData?.region?.length, dispatch]);

  const handleFetchStates = useCallback(() => {
    if (!publicData?.stateUT?.length) {
      dispatch(publicStateUT());
    }
  }, [publicData?.stateUT?.length, dispatch]);

  const handleFetchEnergyTypes = useCallback(() => {
    if (!publicData?.publicEnergyType?.length) {
      dispatch(publicEnergyType());
    }
  }, [publicData?.publicEnergyType?.length, dispatch]);

  const handleFetchSpecifications = useCallback(() => {
    if (!publicData?.specification?.length) {
      dispatch(publicSpecefication());
    }
  }, [publicData?.specification?.length, dispatch]);

  const handleFetchVoltages = useCallback(() => {
    if (!publicData?.voltage?.length) {
      dispatch(publicVoltage());
    }
  }, [publicData?.voltage?.length, dispatch]);

  const handleFetchDiscoms = useCallback(() => {
    if (!publicData?.discom?.length) {
      dispatch(publicDiscom());
    }
  }, [publicData?.discom?.length, dispatch]);

  const handleFetchControlAreas = useCallback(() => {
    if (!publicData?.controlArea?.length) {
      dispatch(publicControlArea());
    }
  }, [publicData?.controlArea?.length, dispatch]);

  useEffect(() => {
    if (editMode && entityData) {
      const detail = initialGeneratorDetail();
      setGeneratorDetails(Array.isArray(detail) ? detail : [detail]);

      const generatorData =
        entityData.data?.data || entityData.data || entityData;

      const initialDiscomVoltage = (
        Array.isArray(detail) ? detail : [detail]
      ).map((d) => {
        const firstDiscom =
          Array.isArray(d.discom) && d.discom.length > 0 ? d.discom[0] : null;
        return {
          discomId: firstDiscom?.discomId ?? null,
          voltage: firstDiscom?.voltage ?? null,
        };
      });
      setDiscomVoltage(
        initialDiscomVoltage.length
          ? initialDiscomVoltage
          : [{ discomId: null, voltage: null }],
      );

      const officeAddress = generatorData.generatorOfficeAddress;
      const correspondenceAddress =
        generatorData.generatorCorrespondenceAddress;

      const addressesAreSame =
        officeAddress.address === correspondenceAddress.address &&
        officeAddress.stateUtId === correspondenceAddress.stateUtId &&
        officeAddress.cityId === correspondenceAddress.cityId &&
        officeAddress.pinCode === correspondenceAddress.pinCode &&
        officeAddress.email === correspondenceAddress.email &&
        officeAddress.contactPersonName ===
          correspondenceAddress.contactPersonName &&
        officeAddress.mobileNumber === correspondenceAddress.mobileNumber &&
        officeAddress.alternateMobileNumber ===
          correspondenceAddress.alternateMobileNumber;

      setSameCheck(addressesAreSame ? "same" : "different");
    }
  }, [editMode, entityData, dispatch]);

  useEffect(() => {
    if (!(editMode && entityData)) return;

    const generatorData = entityData.data?.data || entityData;
    const officeAddress = generatorData.generatorOfficeAddress || {};
    const correspondenceAddress =
      generatorData.generatorCorrespondenceAddress || {};

    if (
      officeAddress.stateUtId &&
      (!publicData?.city || publicData.city.length === 0)
    ) {
      dispatch(publicCityById(officeAddress.stateUtId));
    }

    if (
      correspondenceAddress.stateUtId &&
      correspondenceAddress.stateUtId !== officeAddress.stateUtId &&
      (!publicData?.city || publicData.city.length === 0)
    ) {
      dispatch(publicCityById(correspondenceAddress.stateUtId));
    }

    if (
      generatorData.stateUtId &&
      (!publicData?.discom || publicData.discom.length === 0)
    ) {
      dispatch(publicDiscomByStateId(generatorData.stateUtId));
    }
  }, [
    editMode,
    entityData,
    publicData?.city?.length,
    publicData?.discom?.length,
    dispatch,
  ]);

  useEffect(() => {
    if (editMode && entityData && publicData?.publicEnergyType?.length > 0) {
      setGeneratorDetails((prevDetails) => {
        if (!prevDetails[0]?.genId?.id) return prevDetails;

        const selectedEnergyType = publicData.publicEnergyType.find(
          (energyType) => energyType.id === prevDetails[0].genId.id,
        );

        if (selectedEnergyType && prevDetails[0].genType === "") {
          const updatedDetails = [...prevDetails];
          if (updatedDetails[0]) {
            updatedDetails[0].genType = selectedEnergyType.genType;
          }
          return updatedDetails;
        }

        return prevDetails;
      });
    }
  }, [editMode, entityData, publicData?.publicEnergyType]);

  useEffect(() => {
    handleFetchRegions();
    handleFetchStates();
    handleFetchVoltages();
    handleFetchDiscoms();
    handleFetchControlAreas();
    handleFetchSpecifications();
    handleFetchEnergyTypes();
  }, []);

  useEffect(() => {
    const targetCompanyId = companyId || companyIdFromUrl || companyConGenId;

    if (!targetCompanyId) return;

    getWalletAccountsByCompanies([targetCompanyId])
      .then((res) => {
        const fetchedCompanyData = res?.data?.data?.find(
          (item) => item.companyId == targetCompanyId, // Use == for loose comparison
        );
        setConsumerOptions(fetchedCompanyData?.consumerWalletAccounts || []);
        setGeneratorOptions(fetchedCompanyData?.generatorWalletAccounts || []);
        setCompanyData(fetchedCompanyData); // Add this line
      })
      .catch((err) => {
        console.error("Error fetching wallet accounts: ", err);
        setConsumerOptions([]);
        setGeneratorOptions([]);
        setCompanyData(null); // Add this line
      });
  }, [companyId, companyIdFromUrl, companyConGenId]);

  useEffect(() => {
    if (activeRelation !== "SAME") return;

    const entityId = sameAsCompanyData?.id;
    const entityType = sameAsCompanyData?.type?.toLowerCase();

    if (!entityId || !entityType) return;

    const prefilledData = async () => {
      try {
        let bankDetails, gst, pan, tan, documents;

        if (entityType === "consumer") {
          const res = await dispatch(fetchConsumer(entityId)).unwrap();
          const consumer = res?.consumerDetail?.data?.data;
          bankDetails = consumer?.consumerBankDetails;
          documents = consumer?.consumerDocuments;
          gst = consumer?.gst;
          pan = consumer?.pan;
          tan = consumer?.tan;
        } else if (entityType === "generator") {
          const res = await dispatch(fetchGeneratorDetails(entityId)).unwrap();
          const generator = res?.generatorDetail?.data?.data;
          bankDetails = generator?.generatorBankDetails;
          documents = generator?.generatorDocuments;
          gst = generator?.gst;
          pan = generator?.pan;
          tan = generator?.tan;
        } else {
          return;
        }

        if (!bankDetails) return;

        setGeneratorDetails((prev) => {
          const updated = [...prev];
          updated[0] = {
            ...updated[0],
            bankName: bankDetails?.bankName || "",
            branchAddress: bankDetails?.branchAddress || "",
            accountNumber: bankDetails?.accountNumber || "",
            ifscCode: bankDetails?.ifscCode || "",
            gst: gst || "",
            pan: pan || "",
            tan: tan || "",
            gstDocId: documents?.gstDocId || "",
            panDocId: documents?.panDocId || "",
            tanDocId: documents?.tanDocId || "",
            bankDifferent: true,
          };
          return updated;
        });
      } catch (error) {
        console.error("Failed to prefill data", error);
      }
    };

    prefilledData();
  }, [
    activeRelation,
    sameAsCompanyData?.id,
    sameAsCompanyData?.type,
    dispatch,
  ]);
  useEffect(() => {
    if (editMode) return;
    if (activeRelation !== "DIFFERENT") return;

    setGeneratorDetails((prev) => {
      const updated = [...prev];

      updated[0] = {
        ...updated[0],
        bankName: "",
        branchAddress: "",
        accountNumber: "",
        ifscCode: "",

        gst: "",
        pan: "",
        tan: "",

        gstDocId: "",
        panDocId: "",
        tanDocId: "",

        bankDifferent: false,
      };

      return updated;
    });
  }, [activeRelation, editMode]);

  const handleChange = useCallback(
    async (index, type, value) => {
      const updatedGeneratorDetails = [...generatorDetails];
      updatedGeneratorDetails[index][type] = value;

      const updatedErrors = { ...errors };

      if (type === "genType") {
        updatedGeneratorDetails[index].genId = null;
        delete updatedErrors[`genId_${index}`];
      }

      if (type === "installCap" && !updatedGeneratorDetails[index].plantName) {
        updatedErrors[`plantName_${index}`] = "Generator Name is required.";
      } else {
        delete updatedErrors[`plantName_${index}`];
      }

      if (type === "plantName") {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

        debounceTimeout.current = setTimeout(async () => {
          const currentName = updatedGeneratorDetails[index]?.plantName?.trim();
          if (currentName) {
            const response = await verifyGeneratorName(currentName);
            const decryptedData = decryptData(response?.data);

            setErrors((prevErrors) => {
              const newErrors = { ...prevErrors };
              if (decryptedData?.code !== 200) {
                newErrors[`plantName_${index}`] = decryptedData?.error;
              } else {
                delete newErrors[`plantName_${index}`];
              }
              return newErrors;
            });
          }
        }, 5000);
      }

      if (type === "state" && value?.id) {
        dispatch(publicDiscomByStateId(value.id));
      }

      setGeneratorDetails(updatedGeneratorDetails);
      setErrors(updatedErrors);

      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`${type}_${index}`];
        return newErrors;
      });
    },
    [generatorDetails, errors, dispatch],
  );

  const handleDiscomChange = useCallback(
    (index, type, value) => {
      const updatedDiscomVoltage = [...discomVoltage];
      updatedDiscomVoltage[index] = {
        ...updatedDiscomVoltage[index],
        [type]: value,
      };

      if (type === "discomId") {
        // Reset voltage when discom changes
        updatedDiscomVoltage[index].voltage = null;

        // Extract display value (string) for generatorDetails
        const discomDisplayValue = value?.name || value?.acronym || value || "";
        handleChange(index, "discomName", discomDisplayValue);
      }

      setDiscomVoltage(updatedDiscomVoltage);

      if (type === "voltage" && value?.id) {
        const updatedGeneratorDetails = [...generatorDetails];
        const selectedDiscom = updatedDiscomVoltage[index]?.discomId;

        if (!selectedDiscom) {
          console.error("No discom selected when trying to add voltage");
          return;
        }

        // Check for duplicate voltage for same discom
        const duplicateVoltageForDiscom = updatedGeneratorDetails[
          index
        ].discom?.find(
          (item) =>
            item.discomId?.id === selectedDiscom?.id &&
            item.voltage?.id === value?.id,
        );

        if (duplicateVoltageForDiscom) {
          toast.error(
            `Voltage level ${value.level} is already selected for this DISCOM`,
            toastProperties,
          );
          return;
        }

        const discomObject = {
          discomId: selectedDiscom,
          voltage: value,
        };

        if (!Array.isArray(updatedGeneratorDetails[index].discom)) {
          updatedGeneratorDetails[index].discom = [];
        }
        updatedGeneratorDetails[index].discom = [
          ...updatedGeneratorDetails[index].discom,
          discomObject,
        ];

        // Reset form after successful addition
        updatedDiscomVoltage[index] = { discomId: null, voltage: null };
        setDiscomVoltage(updatedDiscomVoltage);
        setGeneratorDetails(updatedGeneratorDetails);

        // Clear voltage error since we now have at least one discom voltage
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`voltage_${index}`];
          return newErrors;
        });
      }
    },
    [discomVoltage, handleChange, generatorDetails],
  );

  const handleAddUser = () => {
    const lastGenerator =
      generatorDetails[generatorDetails.length - 1] || initialGeneratorDetail();

    const newGenerator = { ...lastGenerator };
    setGeneratorDetails((prev) => [...prev, newGenerator]);
    setDiscomVoltage((prev) => {
      const lastDiscomVoltage = prev[prev.length - 1] || {
        discomId: {},
        voltage: {},
      };
      return [...prev, { ...lastDiscomVoltage }];
    });
  };

  const handleRemoveUser = (index) => {
    if (generatorDetails.length > 1) {
      const updatedDetails = generatorDetails.filter((_, i) => i !== index);
      const updatedDiscomVoltage = discomVoltage.filter((_, i) => i !== index);

      setGeneratorDetails(updatedDetails);
      setDiscomVoltage(updatedDiscomVoltage);

      const updatedErrors = { ...errors };
      Object.keys(updatedErrors).forEach((key) => {
        if (key.endsWith(`_${index}`)) {
          delete updatedErrors[key];
        }

        if (key.includes("_") && parseInt(key.split("_").pop()) > index) {
          const newKey = key.replace(
            /_\d+$/,
            `_${parseInt(key.split("_").pop()) - 1}`,
          );
          updatedErrors[newKey] = updatedErrors[key];
          delete updatedErrors[key];
        }
      });
      setErrors(updatedErrors);
    }
  };

  const controlAreaOptions =
    publicData?.controlArea?.map((item) => ({
      id: typeof item === "string" ? item : item.id,
      name: typeof item === "string" ? item : item.name,
    })) || [];

  const handleGetStateByRegionId = useCallback(
    (id) => {
      dispatch(publicStateByRegionId(id?.id));
    },
    [dispatch],
  );

  const handleGetDiscomByStateId = useCallback(
    (id) => {
      dispatch(publicDiscomByStateId(id?.id));
    },
    [dispatch],
  );

  const handleFieldChange = useCallback(
    (index, field, value) => {
      const updatedDetails = [...generatorDetails];
      updatedDetails[index][field] = value;
      setGeneratorDetails(updatedDetails);

      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`${field}_${index}`];
        return newErrors;
      });
    },
    [generatorDetails],
  );

  const formatDateForAPI = (dateValue) => {
    if (!dateValue || dateValue === "") return null;

    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Date formatting error:", error);
      return null;
    }
  };

  const transformToApiPayload = () => {
    const generatorData = generatorDetails[0] || {};
    const existingData =
      editMode && entityData
        ? entityData.data?.data || entityData.data || entityData
        : {};

    const basePayload = {
      generatorId: editMode ? existingData.generatorId || null : null,
      companyId: companyId || (editMode ? existingData.companyId : null),
      uniqueId: editMode
        ? existingData.uniqueId
        : `GEN${Date.now()}${Math.floor(Math.random() * 1000)}`,
      generatorName: generatorData.plantName || "",
      isCommissioned: generatorData.plant === "commissioned",
      cloneConsumerId:
        sameAsCompanyData?.type?.toLowerCase() === "consumer"
          ? sameAsCompanyData?.id
          : null,
      cloneGeneratorId:
        sameAsCompanyData?.type?.toLowerCase() === "generator"
          ? sameAsCompanyData?.id
          : null,
    };

    if (isSuperAdmin && authUser) {
      const userId =
        authUser.userId ||
        authUser.id ||
        authUser.user?.userId ||
        authUser.user?.id ||
        authUser.userDetails?.userId ||
        authUser.userDetails?.id;
      if (userId) {
        basePayload.userId = userId;
      } else {
        console.warn("No valid userId found in authUser:", authUser);
      }
    }

    const payload = {
      ...basePayload,
      energyTypeId: generatorData.genId?.id
        ? parseInt(generatorData.genId.id)
        : null,
      specificationId: generatorData.specification?.id
        ? parseInt(generatorData.specification.id)
        : null,
      maxInjCap: parseFloat(generatorData.injectionCap || 0),
      installedCap: parseFloat(generatorData.installCap || 0),
      isSubsidyReceived: generatorData.subsidy === "yes",
      subsidyType: generatorData.subsidyType || "",
      isAbtExist: generatorData.abt === "yes",
      controlAreaId: generatorData.controlArea?.id
        ? parseInt(generatorData.controlArea.id)
        : null,
      pointValue: generatorData.poi || "STU",
      poiLocation: generatorData.poiLocation || "",
      stateUtId: generatorData.state?.id
        ? parseInt(generatorData.state.id)
        : null,
      pan: generatorData.pan || "",
      tan: generatorData.tan || "",
      gst: generatorData.gst || "",

      generatorBankDetails: {
        bankAccountId: editMode
          ? existingData.generatorBankDetails?.bankAccountId || null
          : null,
        companyId: editMode ? existingData.companyId || null : null,
        generatorId: editMode ? existingData.generatorId || null : null,
        accountHolderName: generatorData.plantName || "",
        accountNumber: generatorData.accountNumber || "",
        ifscCode: generatorData.ifscCode || "",
        bankName: generatorData.bankName || "",
        branchAddress: editMode
          ? existingData.branchName || generatorData.branchAddress || ""
          : generatorData.branchAddress || "",
      },

      istsVoltageLevel: (generatorData.istsVoltage || []).map((voltage) =>
        typeof voltage === "object" ? voltage.level : voltage,
      ),
      stuVoltageLevel: (generatorData.stuVoltage || []).map((voltage) =>
        typeof voltage === "object" ? voltage.level : voltage,
      ),

      discomDetails: (generatorData.discom || [])
        .filter((discom) => discom && discom.discomId && discom.voltage)
        .map((discom) => ({
          discomId:
            typeof discom.discomId === "object"
              ? discom.discomId.id
              : discom.discomId,
          voltageLevel:
            typeof discom.voltage === "object"
              ? discom.voltage.level || discom.voltage.name
              : discom.voltage,
        })),

      generatorDocuments: {
        documentMappingId: editMode
          ? existingData.generatorDocuments?.documentMappingId || null
          : null,
        companyId: editMode ? existingData.companyId || null : null,
        consumerId: null,
        generatorId: editMode ? existingData.generatorId || null : null,
        panDocId: editMode
          ? existingData.generatorDocuments?.panDocId || null
          : null,
        tanDocId: editMode
          ? existingData.generatorDocuments?.tanDocId || null
          : null,
        gstDocId: editMode
          ? existingData.generatorDocuments?.gstDocId || null
          : null,
        membershipFormDocId: editMode
          ? existingData.generatorDocuments?.membershipFormDocId || null
          : null,
        undertakingDocId: editMode
          ? existingData.generatorDocuments?.undertakingDocId || null
          : null,
        proofOfPaymentDocId: editMode
          ? existingData.generatorDocuments?.proofOfPaymentDocId || null
          : null,
        tradingLicenseDocId: editMode
          ? existingData.generatorDocuments?.tradingLicenseDocId || null
          : null,
        nocDocs: editMode
          ? existingData.generatorDocuments?.nocDocs || null
          : null,
        otherFiles: editMode
          ? existingData.generatorDocuments?.otherFiles || null
          : null,
        aadhaarCard: "",
        panCard: "",
        gstCertificate: "",
      },

      generatorOfficeAddress: {
        addressId: editMode
          ? existingData.generatorOfficeAddress?.addressId || null
          : null,
        companyId: editMode ? existingData.companyId || null : null,
        consumerId: null,
        generatorId: editMode ? existingData.generatorId || null : null,
        type: "OFFICE",
        address: generatorData.registered_address || "",
        stateUtId: generatorData.registered_state?.id
          ? parseInt(generatorData.registered_state.id)
          : null,
        cityId: generatorData.registered_city?.id
          ? parseInt(generatorData.registered_city.id)
          : null,
        pinCode: generatorData.registered_pincode
          ? parseInt(generatorData.registered_pincode)
          : null,
        email: generatorData.registered_email || "",
        contactPersonName: generatorData.registered_contact || "",
        mobileNumber: generatorData.registered_mobile || "",
        alternateMobileNumber: generatorData.registered_altMobile || "",
      },

      generatorCorrespondenceAddress: {
        addressId: editMode
          ? existingData.generatorCorrespondenceAddress?.addressId || null
          : null,
        companyId: editMode ? existingData.companyId || null : null,
        consumerId: null,
        generatorId: editMode ? existingData.generatorId || null : null,
        type: "CORRESPONDENCE",

        address:
          sameCheck === "same"
            ? generatorData.registered_address || ""
            : generatorData.correspondence_address || "",
        stateUtId:
          sameCheck === "same"
            ? generatorData.registered_state?.id
              ? parseInt(generatorData.registered_state.id)
              : null
            : generatorData.correspondence_state?.id
              ? parseInt(generatorData.correspondence_state.id)
              : null,
        cityId:
          sameCheck === "same"
            ? generatorData.registered_city?.id
              ? parseInt(generatorData.registered_city.id)
              : null
            : generatorData.correspondence_city?.id
              ? parseInt(generatorData.correspondence_city.id)
              : null,
        pinCode:
          sameCheck === "same"
            ? generatorData.registered_pincode
              ? parseInt(generatorData.registered_pincode)
              : null
            : generatorData.correspondence_pincode
              ? parseInt(generatorData.correspondence_pincode)
              : null,
        email:
          sameCheck === "same"
            ? generatorData.registered_email || ""
            : generatorData.correspondence_email || "",
        contactPersonName:
          sameCheck === "same"
            ? generatorData.registered_contact || ""
            : generatorData.correspondence_contact || "",
        mobileNumber:
          sameCheck === "same"
            ? generatorData.registered_mobile || ""
            : generatorData.correspondence_mobile || "",
        alternateMobileNumber:
          sameCheck === "same"
            ? generatorData.registered_altMobile || ""
            : generatorData.correspondence_altMobile || "",
      },
      isCompanyWallet:
        sameAsCompanyData?.type?.toLowerCase() === "company" ? true : false,
      createdAt: editMode ? existingData.createdAt : null,
      userCount: editMode ? existingData.userCount || 0 : 0,
      accountStatus: editMode
        ? existingData.accountStatus || "VERIFIED"
        : "VERIFIED",
    };

    const formattedDate = formatDateForAPI(generatorData.plantDate);
    if (formattedDate) {
      payload.commissionedAt = formattedDate;
    }

    return payload;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let hasErrors = false;
    generatorDetails.forEach((generator, index) => {
      if (!generator.plantName?.trim()) {
        newErrors[`plantName_${index}`] = "Generator Name is required";
        hasErrors = true;
      }
      if (!generator.plant?.trim()) {
        newErrors[`plant_${index}`] = "Plant is required";
        hasErrors = true;
      }
      if (
        !generator.plantDate ||
        (typeof generator.plantDate === "string" && !generator.plantDate.trim())
      ) {
        newErrors[`plantDate_${index}`] = "Plant Date is required";
        hasErrors = true;
      }
      if (!generator.genType?.trim()) {
        newErrors[`genType_${index}`] = "Generating Type is required";
        hasErrors = true;
      }
      if (!generator.genId?.id) {
        newErrors[`genId_${index}`] = "Energy Type is required";
        hasErrors = true;
      }
      if (!generator.specification?.id) {
        newErrors[`specification_${index}`] = "Plant Specification is required";
        hasErrors = true;
      }
      if (!generator.installCap?.trim()) {
        newErrors[`installCap_${index}`] = "Installed Capacity is required";
        hasErrors = true;
      }
      if (!generator.injectionCap?.trim()) {
        newErrors[`injectionCap_${index}`] = "Injection Capacity is required";
        hasErrors = true;
      }
      if (!generator.identificationCode?.trim()) {
        newErrors[`identificationCode_${index}`] =
          "Identification Code is required";
        hasErrors = true;
      }
      if (!generator.subsidy?.trim()) {
        newErrors[`subsidy_${index}`] = "Subsidy is required";
        hasErrors = true;
      }
      if (!generator.subsidyType?.trim()) {
        newErrors[`subsidyType_${index}`] = "Subsidy Type is required";
        hasErrors = true;
      }
      if (!generator.abt?.trim()) {
        newErrors[`abt_${index}`] = "ABT Metering Arrangement is required";
        hasErrors = true;
      }
      if (!generator.controlArea?.id) {
        newErrors[`controlArea_${index}`] = "Control Area is required";
        hasErrors = true;
      }
      // if (!generator.poi?.trim()) {
      //   newErrors[`poi_${index}`] = "Point of Injection is required";
      //   hasErrors = true;
      // }
      // if (!generator.poiLocation?.trim()) {
      //   newErrors[`poiLocation_${index}`] = "POI Location is required";
      //   hasErrors = true;
      // }
      // if (!generator.region?.id) {
      //   newErrors[`region_${index}`] = "Region is required";
      //   hasErrors = true;
      // }
      // if (!generator.state?.id) {
      //   newErrors[`state_${index}`] = "State is required";
      //   hasErrors = true;
      // }
      // if (!generator.discom || generator.discom.length === 0) {
      //   newErrors[`discom_${index}`] = "Discom is required";
      //   hasErrors = true;
      // }
      // if (!generator.discomName?.id) {
      //   newErrors[`discomName_${index}`] = "Discom Name is required";
      //   hasErrors = true;
      // }
      if (!generator.bankName?.trim()) {
        newErrors[`bankName_${index}`] = "Bank Name is required";
        hasErrors = true;
      }
      if (!generator.branchAddress?.trim()) {
        newErrors[`branchAddress_${index}`] = "Branch Address is required";
        hasErrors = true;
      }
      if (!generator.accountNumber?.trim()) {
        newErrors[`accountNumber_${index}`] = "Account Number is required";
        hasErrors = true;
      }
      if (!generator.ifscCode?.trim()) {
        newErrors[`ifscCode_${index}`] = "IFSC Code is required";
        hasErrors = true;
      }
      if (!generator.pan?.trim()) {
        newErrors[`pan_${index}`] = "PAN is required";
        hasErrors = true;
      }
      if (!generator.gst?.trim()) {
        newErrors[`gst_${index}`] = "GST is required";
        hasErrors = true;
      }
      if (!generator.tan?.trim()) {
        newErrors[`tan_${index}`] = "TAN is required";
        hasErrors = true;
      }
      // if (!generator.registered_address?.trim()) {
      //   newErrors[`registered_address_${index}`] =
      //     "Registered Address is required";
      //   hasErrors = true;
      // }
      // if (!generator.registered_state?.id) {
      //   newErrors[`registered_state_${index}`] = "Registered State is required";
      //   hasErrors = true;
      // }
      // if (!generator.registered_city?.id) {
      //   newErrors[`registered_city_${index}`] = "Registered City is required";
      //   hasErrors = true;
      // }
      // if (!generator.registered_pincode?.trim()) {
      //   newErrors[`registered_pincode_${index}`] =
      //     "Registered Pincode is required";
      //   hasErrors = true;
      // }
      // if (!generator.registered_email?.trim()) {
      //   newErrors[`registered_email_${index}`] = "Registered Email is required";
      //   hasErrors = true;
      // }
      // if (!generator.registered_contact?.trim()) {
      //   newErrors[`registered_contact_${index}`] =
      //     "Contact Person Name is required";
      //   hasErrors = true;
      // }
      // if (!generator.registered_mobile?.trim()) {
      //   newErrors[`registered_mobile_${index}`] = "Mobile Number is required";
      //   hasErrors = true;
      // }
      // if (sameCheck !== "same") {
      //   if (!generator.correspondence_address?.trim()) {
      //     newErrors[`correspondence_address_${index}`] =
      //       "Correspondence Address is required";
      //     hasErrors = true;
      //   }
      //   if (!generator.correspondence_state?.id) {
      //     newErrors[`correspondence_state_${index}`] =
      //       "Correspondence State is required";
      //     hasErrors = true;
      //   }
      //   if (!generator.correspondence_city?.id) {
      //     newErrors[`correspondence_city_${index}`] =
      //       "Correspondence City is required";
      //     hasErrors = true;
      //   }
      //   if (!generator.correspondence_pincode?.trim()) {
      //     newErrors[`correspondence_pincode_${index}`] =
      //       "Correspondence Pincode is required";
      //     hasErrors = true;
      //   }
      //   if (!generator.correspondence_email?.trim()) {
      //     newErrors[`correspondence_email_${index}`] =
      //       "Correspondence Email is required";
      //     hasErrors = true;
      //   }
      //   if (!generator.correspondence_contact?.trim()) {
      //     newErrors[`correspondence_contact_${index}`] =
      //       "Correspondence Contact Person is required";
      //     hasErrors = true;
      //   }
      //   if (!generator.correspondence_mobile?.trim()) {
      //     newErrors[`correspondence_mobile_${index}`] =
      //       "Correspondence Mobile is required";
      //     hasErrors = true;
      //   }
      // }
    });
    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (!validateAllFields()) {
      toast.error("Please fill in all required fields");
      setLoading(false);
      return;
    }

    try {
      const payload = transformToApiPayload();

      let response;
      if (editMode) {
        if (isSuperAdmin) {
          response = await updateGeneratorSuperAdmin(payload, companyId);
        } else {
          response = await updateGenerator(payload);
        }
      } else {
        if (isSuperAdmin) {
          response = await addGeneratorSuperAdmin([payload], companyId);
        } else {
          const arrayPayload = [payload];
          response = await addGenerator(arrayPayload);
        }

        if (response?.data?.code === 200 || response?.status === 200) {
        } else {
          console.warn("Unexpected response:", response);
        }
      }

      try {
        if (isSuperAdmin) {
          const targetCompanyId = companyId || payload?.companyId;
          if (targetCompanyId) {
            await dispatch(fetchGeneratorsCompanyId(targetCompanyId));
          }
        } else {
          await dispatch(fetchGenerators());
        }
      } catch (refreshError) {
        console.warn("Error refreshing generators list:", refreshError);
      }

      if (handleClose) {
        try {
          handleClose();
        } catch (closeError) {
          console.warn("Error closing form:", closeError);
        }
      }

      dispatch(fetchSideBarGeneratorsList());
    } catch (error) {
      console.error(
        `Error ${editMode ? "updating" : "creating"} generator:`,
        error,
      );

      let errorMessage = `Error ${
        editMode ? "updating" : "creating"
      } generator. Please try again.`;

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderTextInput = (
    index,
    field,
    heading,
    example,
    type = "text",
    readOnly = false,
    disabled = false,
  ) => (
    <TextInput
      heading={`${heading} `}
      value={generatorDetails[index][field]}
      onChange={(value) => handleChange(index, field, value)}
      example={example}
      type={type}
      height="45"
      error={errors[`${field}_${index}`]}
      readOnly={readOnly}
      disabled={readOnly || disabled}
    />
  );

  const renderSelectInput = (
    index,
    type,
    list,
    example,
    heading,
    recurring = null,
    disabled = false,
    onFocusHandler = null,
  ) => {
    // FIX: Correct disabled logic for discom flow
    const isDisabled = (() => {
      if (recurring === "discom") {
        if (type === "discomId") {
          // Discom dropdown: disable ONLY if state not selected
          const hasStateSelected = generatorDetails[index]?.state?.id;
          return !hasStateSelected;
        }
        if (type === "voltage") {
          // Voltage dropdown: disable ONLY if no valid discom selected
          if (!discomVoltage[index]) return true;
          const discomSelected = discomVoltage[index].discomId;
          if (!discomSelected) return true;

          // Valid if it's a string OR object with id/name/acronym
          return typeof discomSelected === "string"
            ? !discomSelected.trim()
            : !discomSelected.id;
        }
      }
      return disabled;
    })();

    // Normalize list items so SelectInput receives simple {id, name} objects or primitives
    const normalize = (ele) => {
      if (ele === null || ele === undefined) return ele;
      if (typeof ele !== "object") return ele;

      const id =
        ele.id ?? ele.discomId ?? (ele.discomId && ele.discomId.id) ?? null;
      const name =
        ele.name ??
        ele.discomName ??
        ele.acronym ??
        ele.level ??
        ele.value ??
        JSON.stringify(ele);

      return { id, name, acronym: ele.acronym };
    };

    // Only normalize discom list entries (keep voltage/energy/state lists intact)
    const normalizedList =
      recurring === "discom" && type === "discomId" && Array.isArray(list)
        ? list.map(normalize)
        : list;

    // Safe value extraction: for discomId prefer primitive id/name; for other fields keep full object
    let safeValue;
    if (recurring === "discom" && type === "discomId") {
      safeValue = discomVoltage[index]?.[type] ?? generatorDetails[index][type];
      if (safeValue && typeof safeValue === "object" && safeValue !== null) {
        safeValue = safeValue.id ?? safeValue.name ?? safeValue.acronym ?? null;
      }
    } else {
      safeValue =
        recurring === "discom"
          ? discomVoltage[index]?.[type]
          : generatorDetails[index][type];
    }

    return (
      <SelectInput
        onChange={(value) => {
          if (recurring === "discom") {
            handleDiscomChange(index, type, value);
          } else {
            handleChange(index, type, value);
            if (type === "region") {
              handleGetStateByRegionId(value);
            } else if (type === "state") {
              handleGetDiscomByStateId(value);
            }
          }
        }}
        list={normalizedList}
        value={safeValue}
        example={example}
        heading={heading}
        height="45px"
        error={errors[`${type}_${index}`]}
        disabled={isDisabled}
        onFocus={onFocusHandler}
      />
    );
  };

  const renderFileUpload = (index, heading, field, example, type, ID) => (
    <Fileupload
      heading={heading}
      value={generatorDetails[index][field]}
      onChange={(value) => handleFieldChange(index, field, value)}
      height="45"
      example={example}
      type={type}
      id={ID}
      error={errors[`${field}_${index}`]}
    />
  );

  const renderCheckbox = (index, field, options, heading) => (
    <div className={Styles.checkBoxElem}>
      <div className={Styles.checkBoxHead}>{heading}</div>
      <div
        className={`${Styles.selectCheckElemEent} ${Styles.checkboxGenerator}`}
      >
        {(Array.isArray(options) ? options : []).map((option) => (
          <div
            key={option.value}
            onClick={() => handleChange(index, field, option.value)}
            style={{ width: `${100 / options.length}%` }}
            className={
              generatorDetails[index][field] === option.value
                ? Styles.activeCheck
                : Styles.inactiveCheck
            }
          >
            {option.label}
          </div>
        ))}
      </div>
      {errors[`${field}_${index}`] && (
        <span className={Styles.errorPass}>{errors[`${field}_${index}`]}</span>
      )}
    </div>
  );

  const renderDateInput = (index, field) => (
    <DateInput
      heading={
        generatorDetails[index].plant === "commissioned"
          ? "Date of commission"
          : "Date to be commission"
      }
      value={generatorDetails[index][field]}
      onChange={(value) => handleChange(index, field, value)}
      height="45"
      error={errors[`${field}_${index}`]}
    />
  );

  const renderInputFields = (index) => {
    return (
      <>
        <div className={Styles.layer3ElementsContainer}>
          {renderTextInput(
            index,
            "plantName",
            "Generator Name",
            "Eg. GNA Energy",
          )}
          {renderTextInput(
            index,
            "installCap",
            "Installed Capacity (In MW)",
            "Eg. 100000",
          )}
          {renderTextInput(
            index,
            "injectionCap",
            "Maximum Injection Capacity (MW)",
            "Eg. 100000",
          )}
        </div>
        <div className={Styles.layer3ElementsContainer}>
          {renderSelectInput(
            index,
            "specification",
            publicData?.specification?.filter(
              (ele) => ele?.type === "GENERATOR",
            ),
            "Select Plant Specification",
            "Plant Specification",
            null,
            false,
            handleFetchSpecifications,
          )}

          {renderCheckbox(
            index,
            "plant",
            [
              { value: "commissioned", label: "Commissioned" },
              { value: "notcommissioned", label: "To Be Commissioned" },
            ],
            "Plant",
          )}

          {renderDateInput(index, "plantDate")}
        </div>
        <div className={Styles.layer3ElementsContainer}>
          {renderCheckbox(
            index,
            "genType",
            [
              { value: "RENEWABLE", label: "Renewable" },
              { value: "NON_RENEWABLE", label: "Non Renewable" },
            ],
            "Generating Type",
          )}
          {renderSelectInput(
            index,
            "genId",
            publicData?.publicEnergyType?.filter(
              (ele) => ele?.genType === generatorDetails[index].genType,
            ),
            "Select Energy Type",
            "Energy Type",
            null,
            false,
            handleFetchEnergyTypes,
          )}
          {renderTextInput(
            index,
            "identificationCode",
            "Unique Project Identification Code",
            "Eg. GNA2025",
          )}
        </div>
        <div className={Styles.layer3ElementsContainer}>
          {renderCheckbox(
            index,
            "subsidy",
            [
              { value: "NO", label: "No" },
              { value: "YES", label: "Yes" },
            ],
            "Subsidy Received",
          )}
          {renderTextInput(
            index,
            "subsidyType",
            "Subsidy Type",
            "Enter Subsidy Type",
          )}
          {renderCheckbox(
            index,
            "abt",
            [
              { value: "EXISTS", label: "Exists" },
              { value: "NON_EXISTS", label: "To Be Arranged" },
            ],
            "ABT Metering Arrangement",
          )}
        </div>
        <div className={Styles.layer3ElementsContainer}>
          {renderSelectInput(
            index,
            "controlArea",
            controlAreaOptions,
            "Eg. Select Control Area",
            "Control Area",
            null,
            false,
            handleFetchControlAreas,
          )}
          {renderTextInput(
            index,
            "poiLocation",
            "Name of Location of POI",
            "Enter POI",
          )}
          {renderSelectInput(
            index,
            "region",
            publicData?.region,
            "Select Region",
            "Region",
            null,
            false,
            handleFetchRegions,
          )}
        </div>
        <div className={Styles.layer3ElementsContainer}>
          <div className={Styles.checkBoxElem}>
            <div className={Styles.checkBoxHead}>Point Of Injection (POI)</div>
            <div className={Styles.selectCheckElemEent}>
              {["ISTS", "STU", "DISCOM"].map((poiType) => (
                <div
                  key={poiType}
                  onClick={() => handleChange(index, "poi", poiType)}
                  style={{ width: "30%" }}
                  className={
                    generatorDetails[index].poi === poiType
                      ? Styles.activeCheck
                      : Styles.inactiveCheck
                  }
                >
                  {poiType}
                </div>
              ))}
            </div>
            {errors[`poi_${index}`] && (
              <span className={Styles.errorPass}>{errors[`poi_${index}`]}</span>
            )}
          </div>

          {/* Voltage Selection */}
          <div>
            {(generatorDetails[index].poi === "ISTS" ||
              generatorDetails[index].poi === "STU") && (
              <SelectInput
                onChange={(value) => {
                  const updatedVoltages = [...generatorDetails];
                  const voltageType = `${generatorDetails[
                    index
                  ].poi.toLowerCase()}Voltage`;

                  // Get current voltage list
                  const currentVoltages =
                    updatedVoltages[index][voltageType] || [];

                  // Check if voltage is already selected
                  const isAlreadySelected = currentVoltages.some(
                    (v) => (v?.id || v) === (value?.id || value),
                  );

                  if (isAlreadySelected) {
                    // Remove voltage if already selected
                    updatedVoltages[index][voltageType] =
                      currentVoltages.filter(
                        (v) => (v?.id || v) !== (value?.id || value),
                      );
                  } else {
                    // Add voltage if not already selected
                    updatedVoltages[index][voltageType] = [
                      ...currentVoltages,
                      value,
                    ];
                  }

                  setGeneratorDetails(updatedVoltages);
                }}
                list={publicData?.voltage}
                value={{}}
                example={"Select Voltage Levels"}
                heading={`${generatorDetails[index].poi} Voltage`}
                height={"45px"}
                multiple
                onFocus={handleFetchVoltages}
              />
            )}
          </div>

          {/* Voltage Display */}
          {(generatorDetails[index].poi === "ISTS" ||
            generatorDetails[index].poi === "STU") && (
            <div className={Styles.layerscrollElements}>
              {(Array.isArray(
                generatorDetails[index][
                  `${generatorDetails[index].poi.toLowerCase()}Voltage`
                ],
              )
                ? generatorDetails[index][
                    `${generatorDetails[index].poi.toLowerCase()}Voltage`
                  ]
                : []
              )?.map((ele, voltageIndex) => (
                <div key={voltageIndex} className={Styles.eachLevelLayer}>
                  <span>{ele?.level || ele}</span>
                  <Close
                    style={{ fontSize: "14px", cursor: "pointer" }}
                    onClick={() => {
                      const updatedVoltages = [...generatorDetails];
                      const voltageType = `${generatorDetails[
                        index
                      ].poi.toLowerCase()}Voltage`;
                      updatedVoltages[index][voltageType] = updatedVoltages[
                        index
                      ][voltageType].filter((_, i) => i !== voltageIndex);
                      setGeneratorDetails(updatedVoltages);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={Styles.divider}></div>

        {/* Discom Selection */}
        <div className={Styles.discomSelectionContainer}>
          <div className={Styles.subConterntHeader}>Discom Voltage</div>
          <div className={Styles.layer3ElementsContainer}>
            {renderSelectInput(
              index,
              "state",
              publicData?.stateUT,
              "Select State",
              "State",
              null,
              false,
              handleFetchStates,
            )}
            {renderSelectInput(
              index,
              "discomId",
              publicData?.discom,
              "Select Discom",
              "Discom",
              "discom",
              false,
              handleFetchDiscoms,
            )}
            {renderSelectInput(
              index,
              "voltage",
              publicData?.voltage,
              "Select Voltage Level",
              "Voltage Level",
              "discom",
              false,
              handleFetchVoltages,
            )}
          </div>

          <div className={Styles.voltageOverflow}>
            {generatorDetails[index]?.discom?.map((ele, discomIndex) => (
              <div key={discomIndex} className={Styles.voltageSection}>
                <div className={Styles.actVoltageLevel}>
                  <div>{ele?.voltage?.level}</div>
                  <Close
                    style={{
                      fontSize: "12px",
                      color: "red",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      const updatedGeneratorDetails = [...generatorDetails];
                      updatedGeneratorDetails[index].discom =
                        updatedGeneratorDetails[index].discom.filter(
                          (_, i) => i !== discomIndex,
                        );
                      setGeneratorDetails(updatedGeneratorDetails);
                    }}
                  />
                </div>
                <div className={Styles.discomName}>
                  {ele?.discomId?.acronym}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={isModal ? Styles.modalElementMain : Styles.elementMain}>
      <div className={isModal ? Styles.modalModalHeader : Styles.modalHeader}>
        <div>
          <div className={Styles.entityTabs}>
            <div>{editMode ? "Edit Entity" : "Add Entity"}</div>
            {!hideSelection && (
              <>
                {!editMode && (
                  <div
                    className={Styles.entityTab}
                    onClick={() => onSwitchToConsumer && onSwitchToConsumer()}
                  >
                    Consumer
                  </div>
                )}
                {!editMode && (
                  <div className={`${Styles.entityTab} ${Styles.activeTab}`}>
                    Generator
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div style={{ width: "4%" }}>
          <Close
            onClick={handleClose}
            style={{ cursor: "pointer", marginLeft: "auto", color: "#777777" }}
          />
        </div>
      </div>

      {/* Form Content with Fixed Layout */}
      <div
        className={
          isModal
            ? Styles.modalCompanyAdminFormLayout
            : Styles.companyAdminFormLayout
        }
      >
        {/* Form Content - Scrollable Area */}

        <div className={Styles.companyAdminFormContent}>
          {generatorDetails.map((_, index) => (
            <div className={Styles.multiLayerContainer} key={index}>
              {/* {!editMode && (
                <div
                  className={`${Styles.alterSection} ${Styles.alterSectionOther}`}
                >
                  {generatorDetails.length > 1 && (
                    <div
                      className={Styles.deleteSection}
                      onClick={() => handleRemoveUser(index)}
                    >
                      <RemoveIcon style={{ fontSize: "16px" }} />
                    </div>
                  )}
                  <div
                    className={`${Styles.addSection} ${Styles.AddnewGenSection}`}
                    onClick={handleAddUser}
                  >
                    <Add style={{ fontSize: "14px" }} />
                    <span className={Styles.addNewHeading}>
                      Add New Generator
                    </span>
                  </div>
                </div>
              )} */}
              {renderInputFields(index)}
              <div className={Styles.corrAddressContainer}>
                <div
                  className={`${Styles.subConterntHeader} ${Styles.corrOtherHeading}`}
                >
                  Bank Details
                </div>
                <div className={Styles.button_Section}>
                  <div className={Styles.dropdownWrapper}>
                    <span
                      className={
                        activeRelation === "SAME"
                          ? Styles.active
                          : Styles.inActive
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick("SAME");
                        setActiveRelation("SAME");
                        setOpenDropdown(
                          openDropdown === "SAME" ? null : "SAME",
                        );
                      }}
                    >
                      {sameAsCompanyData
                        ? `${sameAsCompanyData.type} - ${sameAsCompanyData.data}`
                        : "Existing"}
                    </span>

                    {openDropdown === "SAME" && (
                      <div className={Styles.dropdownBox}>
                        {isLoading ? (
                          <div className={Styles.dropdownItem}>Loading...</div>
                        ) : error ? (
                          <div className={Styles.dropdownItem}>{error}</div>
                        ) : companyData ? (
                          <>
                            {companyData?.consumerWalletAccounts?.map((i) => {
                              return (
                                <div
                                  key={i?.consumerId}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(false);
                                    setActiveRelation("SAME");
                                    setSameAsCompanyData({
                                      type: "CONSUMER",
                                      data: i?.consumerName,
                                      id: i?.consumerId,
                                    });
                                  }}
                                  className={Styles.dropdownItem}
                                >
                                  <strong>Consumer:</strong>{" "}
                                  {i?.consumerName || "-"}
                                </div>
                              );
                            })}
                            {companyData?.generatorWalletAccounts?.map((i) => {
                              return (
                                <div
                                  key={i?.generatorId}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(false);
                                    setActiveRelation("SAME");
                                    setSameAsCompanyData({
                                      type: "GENERATOR",
                                      data: i?.generatorName,
                                      id: i?.generatorId,
                                    });
                                  }}
                                  className={Styles.dropdownItem}
                                >
                                  <strong>Generator:</strong>{" "}
                                  {i?.generatorName || "-"}
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <div className={Styles.dropdownItem}>
                            No company data available
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <span
                    className={
                      activeRelation === "DIFFERENT" ? Styles.active : ""
                    }
                    onClick={() => {
                      setActiveRelation("DIFFERENT");
                      setOpenDropdown(null);
                      setSameAsCompanyData(null);
                    }}
                  >
                    New
                  </span>
                </div>
                <div className={Styles.corrAddSelection}>
                  {/* <div className={Styles.officeSelectionText}>
                    <div>
                      Same as the <b>Company</b> bank details
                    </div>
                    <div className={Styles.sel_button}>
                      <span
                        onClick={() =>
                          handleChange(index, "bankDifferent", false)
                        }
                        className={
                          !generatorDetails[index].bankDifferent
                            ? Styles.active_corr
                            : Styles.inAct_corr
                        }
                      >
                        Yes
                      </span>
                      <span
                        onClick={() =>
                          handleChange(index, "bankDifferent", true)
                        }
                        className={
                          generatorDetails[index].bankDifferent
                            ? Styles.active_corr
                            : Styles.inAct_corr
                        }
                      >
                        No
                      </span>
                    </div>
                  </div> */}
                </div>
              </div>
              <div className={Styles.divider}></div>
              {
                <>
                  <div className={Styles.layer2ElementsContainer}>
                    {renderTextInput(
                      index,
                      "bankName",
                      "Bank Name",
                      "Eg. State Bank of India",
                      "text",
                      activeRelation === "SAME",
                    )}
                    {renderTextInput(
                      index,
                      "branchAddress",
                      "Bank Address",
                      "Eg. 123 Main Street, City",
                      "text",
                      activeRelation === "SAME",
                    )}
                  </div>
                  <div className={Styles.layer2ElementsContainer}>
                    {renderTextInput(
                      index,
                      "accountNumber",
                      "Account Number",
                      "Eg. 1234567890",
                      "number",
                      activeRelation === "SAME",
                    )}
                    {renderTextInput(
                      index,
                      "ifscCode",
                      "IFSC Code",
                      "Eg. SBIN0001234",
                      "text",
                      activeRelation === "SAME",
                    )}
                  </div>
                  <div className={Styles.layer3ElementsContainer}>
                    {renderTextInput(
                      index,
                      "gst",
                      "GST Number",
                      "Eg. 123456789012345",
                      "text",
                      activeRelation === "SAME",
                    )}
                    {renderTextInput(
                      index,
                      "pan",
                      "PAN Number",
                      "Eg. ABCDE1234F",
                      "text",
                      activeRelation === "SAME",
                    )}
                    {renderTextInput(
                      index,
                      "tan",
                      "TAN Number",
                      "Eg. ABCDE1234F",
                      "text",
                      activeRelation === "SAME",
                    )}
                  </div>
                  <div className={Styles.layer3ElementsContainer}>
                    {renderFileUpload(
                      index,
                      "GST Certificate Copy",
                      "gstDocId",
                      "Upload GST (.pdf accepted)",
                      "GST",
                      "gstDocId",
                    )}
                    {renderFileUpload(
                      index,
                      "PAN Card Copy",
                      "panDocId",
                      "Upload PAN (.pdf accepted)",
                      "PAN",
                      "panDocId",
                    )}
                    {renderFileUpload(
                      index,
                      "TAN Card Copy",
                      "tanDocId",
                      "Upload TAN (.pdf accepted)",
                      "TAN",
                      "tanDocId",
                    )}
                  </div>
                </>
              }

              <div className={Styles.conAddressContainer}>
                <div
                  className={`${Styles.subConterntHeader} ${Styles.corrOtherHeading}`}
                >
                  Registered Address
                </div>
                <div className={Styles.divider}></div>
                <AddressSection
                  prefix="registered"
                  addressData={generatorDetails[index]}
                  handleChange={handleChange}
                  errors={errors}
                  publicData={publicData}
                  index={index}
                />
                <div className={Styles.divider}></div>
                <div className={Styles.corrAddressContainer}>
                  <div
                    className={`${Styles.subConterntHeader} ${Styles.corrOtherHeading}`}
                  >
                    Correspondence Address
                  </div>
                  <div className={Styles.corrAddSelection}>
                    <div className={Styles.officeSelectionText}>
                      <div>Same as that of Office Address</div>
                      <div className={Styles.sel_button}>
                        <span
                          onClick={() => setSameCheck("same")}
                          className={
                            sameCheck === "same"
                              ? Styles.active_corr
                              : Styles.inAct_corr
                          }
                        >
                          Yes
                        </span>
                        <span
                          onClick={() => setSameCheck("different")}
                          className={
                            sameCheck === "different"
                              ? Styles.active_corr
                              : Styles.inAct_corr
                          }
                        >
                          No
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={Styles.divider}></div>
                {sameCheck === "different" && (
                  <AddressSection
                    prefix="correspondence"
                    addressData={generatorDetails[index]}
                    handleChange={handleChange}
                    errors={errors}
                    publicData={publicData}
                    index={index}
                  />
                )}
              </div>

              {generatorDetails.length === 0 && (
                <div className={Styles.AddMoreCont} onClick={handleAddUser}>
                  <Add style={{ fontSize: "14px" }} />
                  <span>Add New Generator</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Fixed Bottom Buttons */}
        <div
          className={
            isModal
              ? Styles.modalCompanyAdminButtonSection
              : Styles.companyAdminButtonSection
          }
        >
          <div
            className={`${
              isModal ? Styles.modalBtnSection : Styles.btnSection
            }  ${Styles.prevBtn}`}
            // style={{ width: "10%" }}
            onClick={handleClose}
          >
            Cancel
          </div>
          <div
            className={`${
              isModal ? Styles.modalBtnSection : Styles.btnSection
            } ${Styles.nextBtn}`}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading
              ? editMode
                ? "Updating..."
                : "Creating..."
              : editMode
                ? "Update"
                : "Create"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyAdminGeneratorForm;
